// A run of consecutive thinking / tool_call / tool_result frames. Rendered as
// a quiet borderless column of content-width rows. A chain of ≥2 steps is
// collapsed to a one-line summary by default; expanded, it behaves as an
// accordion — exactly one step body open at a time, following the live step.

import { useEffect, useState } from 'react';
import type { NiuMessage } from '../../protocol/types';
import { ChevronRightIcon } from '../ui/Icons';
import { parseThinking, thinkingHeading } from './parseThinking';
import { StepRow, type StepStatus } from './StepRow';

/** Chains with at least this many steps collapse / show a "hide" toggle. */
const COLLAPSE_THRESHOLD = 2;

type ChainStep =
  | { type: 'thinking'; id: string; text: string }
  | {
      type: 'tool';
      id: string;
      toolName: string;
      input: string;
      output: string | null;
      isError: boolean;
    };

function prettyJSON(input: string): string {
  try {
    return JSON.stringify(JSON.parse(input), null, 2);
  } catch {
    return input;
  }
}

/** `mcp__nanoclaw__schedule_task` → `Schedule Task`; `Bash` stays `Bash`. */
function prettyToolName(name: string): string {
  const bare = name.includes('__') ? name.slice(name.lastIndexOf('__') + 2) : name;
  if (!bare.includes('_')) return bare;
  return bare
    .split('_')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/** Pick a one-line summary of a tool call from its JSON input. */
function toolTarget(input: string): string {
  try {
    const o = JSON.parse(input);
    if (o && typeof o === 'object' && !Array.isArray(o)) {
      const rec = o as Record<string, unknown>;
      for (const k of ['command', 'cmd', 'file_path', 'filePath', 'path', 'pattern', 'query', 'url', 'name']) {
        const v = rec[k];
        if (typeof v === 'string' && v.trim()) return v.trim().replace(/\s+/g, ' ');
      }
      for (const v of Object.values(rec)) {
        if (typeof v === 'string' && v.trim()) return v.trim().replace(/\s+/g, ' ');
      }
    }
  } catch {
    /* not JSON — no summary */
  }
  return '';
}

function buildSteps(messages: NiuMessage[]): ChainStep[] {
  const steps: ChainStep[] = [];
  const callIndexByToolUse = new Map<string, number>();

  for (const m of messages) {
    if (m.payload.kind === 'thinking') {
      steps.push({ type: 'thinking', id: m.id, text: m.payload.data.text });
    } else if (m.payload.kind === 'tool_call') {
      const d = m.payload.data;
      steps.push({ type: 'tool', id: m.id, toolName: d.toolName, input: d.input, output: null, isError: false });
      callIndexByToolUse.set(d.toolUseId, steps.length - 1);
    } else if (m.payload.kind === 'tool_result') {
      const d = m.payload.data;
      const idx = callIndexByToolUse.get(d.toolUseId);
      const call = idx !== undefined ? steps[idx] : undefined;
      if (call && call.type === 'tool') {
        call.output = d.output;
        call.isError = d.isError;
        callIndexByToolUse.delete(d.toolUseId);
      } else {
        steps.push({ type: 'tool', id: m.id, toolName: 'Result', input: '', output: d.output, isError: d.isError });
      }
    }
  }
  return steps;
}

export function ProcessingChain({ messages }: { messages: NiuMessage[] }) {
  const steps = buildSteps(messages);
  const activeStep = steps.find((s) => s.type === 'tool' && s.output === null);
  const activeId = activeStep?.id;
  const collapsible = steps.length >= COLLAPSE_THRESHOLD;

  // Collapsed by default once there are ≥2 steps — unless a step is live.
  const [collapsed, setCollapsed] = useState(collapsible && !activeStep);
  // Accordion: exactly one step body open. Follows the live step.
  const [openId, setOpenId] = useState<string | null>(activeId ?? null);

  useEffect(() => {
    if (activeId) setOpenId(activeId);
  }, [activeId]);

  if (steps.length === 0) return null;

  // Rows visible when not collapsible, or when an expanded collapsible chain.
  const showRows = !collapsible || !collapsed;

  return (
    // Collapsed, the chain is a faint one-line affordance belonging to the
    // message it produced — pull that message up closer than the gap-4 default.
    <div
      className={`flex flex-col items-start animate-fade-in transition-[margin] duration-200 ${
        showRows ? '' : '-mb-2.5'
      }`}
    >
      {collapsible ? (
        <button
          type="button"
          onClick={() => setCollapsed((c) => !c)}
          className="-mx-1.5 flex items-center gap-2 rounded-md px-1.5 py-1 text-text-faint transition-colors hover:bg-elevated/60 hover:text-text-secondary"
        >
          <ChevronRightIcon
            size={12}
            className={`text-text-faint/70 transition-transform duration-200 ${showRows ? 'rotate-90' : ''}`}
          />
          <span className="text-[12.5px] font-medium">
            {showRows ? 'Hide steps' : `${steps.length} steps`}
          </span>
        </button>
      ) : null}
      {/* Rows collapse/expand with an animated grid-rows height transition. */}
      <div
        className={`grid transition-all duration-200 ease-out ${
          showRows ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
        }`}
      >
        <div className="min-h-0 overflow-hidden">
          <div className="flex flex-col items-start">
            {steps.map((step) => (
              <ChainRow
                key={step.id}
                step={step}
                open={openId === step.id}
                onToggle={() => setOpenId((cur) => (cur === step.id ? null : step.id))}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function ChainRow({
  step,
  open,
  onToggle,
}: {
  step: ChainStep;
  open: boolean;
  onToggle: () => void;
}) {
  if (step.type === 'thinking') {
    const { title, body } = parseThinking(step.text);
    return (
      <StepRow
        kind="thinking"
        title={thinkingHeading(title)}
        status="none"
        open={open}
        onToggle={onToggle}
        body={
          <p className="max-w-[42rem] whitespace-pre-wrap break-words text-[12.5px] leading-relaxed text-text-secondary">
            {body}
          </p>
        }
      />
    );
  }

  const pending = step.output === null;
  const status: StepStatus = pending ? 'pending' : step.isError ? 'error' : 'ok';
  const hasBody = Boolean(step.input) || step.output !== null;

  return (
    <StepRow
      kind="tool"
      title={prettyToolName(step.toolName)}
      target={toolTarget(step.input)}
      status={status}
      active={pending}
      open={open}
      onToggle={onToggle}
      body={
        hasBody ? (
          <div className="flex max-w-[42rem] flex-col gap-1.5">
            {step.input ? (
              <pre className="scrollbar-thin max-h-72 overflow-y-auto whitespace-pre-wrap break-words rounded-lg bg-elevated px-2.5 py-2 font-mono text-[11.5px] leading-snug text-text-secondary">{prettyJSON(step.input)}</pre>
            ) : null}
            {step.output !== null ? (
              <pre
                className={`scrollbar-thin max-h-72 overflow-y-auto whitespace-pre-wrap break-words rounded-lg px-2.5 py-2 font-mono text-[11.5px] leading-snug ${
                  step.isError ? 'bg-error-bg text-error-text' : 'bg-elevated text-text-secondary'
                }`}
              >{step.output || '(empty result)'}</pre>
            ) : null}
          </div>
        ) : undefined
      }
    />
  );
}
